import Image from '@tiptap/extension-image';
import { mergeAttributes, type Editor } from '@tiptap/core';

// Bij een <figure> staan de beeld-attributen op de binnenliggende <img>, niet op de figure zelf.
const innerImg = (element: HTMLElement): HTMLElement | null =>
  element.tagName === 'FIGURE' ? element.querySelector('img') : element;

// Leest een attribuut van de img, ook als de parse-regel op de omhullende figure matchte.
const imgAttribute = (name: string) => (element: HTMLElement) =>
  innerImg(element)?.getAttribute(name) ?? null;

const isDataSrc = (element: HTMLElement | null): boolean =>
  (element?.getAttribute('src') ?? '').startsWith('data:');

// Blog-variant van de Image-node: draagt alt, caption en uploading-status.
// Met caption serialiseert de node naar <figure class="blog-figure"><img ...><figcaption>...</figcaption></figure>,
// zonder caption naar een kale <img>. Bestaande blogs met kale <img> parsen ongewijzigd.
export const BlogImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      src: { default: null, parseHTML: imgAttribute('src') },
      alt: { default: null, parseHTML: imgAttribute('alt') },
      title: { default: null, parseHTML: imgAttribute('title') },
      width: { default: null, parseHTML: imgAttribute('width') },
      height: { default: null, parseHTML: imgAttribute('height') },
      caption: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          element.tagName === 'FIGURE'
            ? element.querySelector('figcaption')?.textContent || null
            : null,
        renderHTML: () => ({}),
      },
      uploading: {
        default: false,
        parseHTML: () => false,
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    const { allowBase64 } = this.options;
    return [
      {
        tag: 'figure',
        getAttrs: (element: HTMLElement) => {
          const img = element.querySelector('img');
          if (!img) return false;
          // Zonder allowBase64 weigeren we een figure om een data:-afbeelding heen,
          // net zoals de kale img-regel hieronder die weigert.
          if (!allowBase64 && isDataSrc(img)) return false;
          return null;
        },
      },
      { tag: allowBase64 ? 'img[src]' : 'img[src]:not([src^="data:"])' },
    ];
  },

  // Bewust node-niveau: we bouwen de img-attributen opnieuw op uit node.attrs, zodat caption en
  // uploading nooit als los attribuut lekken. Globale attributen van andere extensies worden hier
  // dus niet meegenomen.
  renderHTML({ node }) {
    const imgAttrs = mergeAttributes(this.options.HTMLAttributes, {
      src: node.attrs.src,
      ...(node.attrs.alt ? { alt: node.attrs.alt } : {}),
      ...(node.attrs.title ? { title: node.attrs.title } : {}),
      ...(node.attrs.width ? { width: node.attrs.width } : {}),
      ...(node.attrs.height ? { height: node.attrs.height } : {}),
      ...(node.attrs.uploading ? { 'data-uploading': 'true' } : {}),
    });

    if (node.attrs.caption) {
      return [
        'figure',
        { class: 'blog-figure' },
        ['img', imgAttrs],
        ['figcaption', {}, node.attrs.caption],
      ];
    }
    return ['img', imgAttrs];
  },
});

// addToHistory: false houdt de wijziging buiten de undo-stapel. Gebruik dat voor wijzigingen die de
// gebruiker niet zelf maakte, zoals het omwisselen van een blob-preview naar de definitieve URL:
// anders draait ctrl+Z die omwisseling terug naar een ingetrokken blob-URL.
export type ImageEditOptions = { addToHistory?: boolean };

type ImageHit = { pos: number; nodeSize: number; attrs: Record<string, unknown> };

function findImage(
  editor: Editor,
  match: (node: { type: { name: string }; attrs: Record<string, unknown> }, pos: number) => boolean
): ImageHit | null {
  let found: ImageHit | null = null;
  editor.state.doc.descendants((node, pos) => {
    if (found) return false;
    if (node.type.name === 'image' && match(node, pos)) {
      found = { pos, nodeSize: node.nodeSize, attrs: node.attrs };
      return false;
    }
    return true;
  });
  return found;
}

// De bySrc-helpers werken op de eerste treffer; een blob-URL is uniek per upload.
export function updateImageAttrsBySrc(
  editor: Editor,
  src: string,
  attrs: Record<string, unknown>,
  options?: ImageEditOptions
): boolean {
  if (editor.isDestroyed) return false;
  const hit = findImage(editor, (node) => node.attrs.src === src);
  if (!hit) return false;
  const { tr } = editor.state;
  tr.setNodeMarkup(hit.pos, undefined, { ...hit.attrs, ...attrs });
  if (options?.addToHistory === false) tr.setMeta('addToHistory', false);
  editor.view.dispatch(tr);
  return true;
}

// De bySrc-helpers werken op de eerste treffer; een blob-URL is uniek per upload.
export function removeImageBySrc(
  editor: Editor,
  src: string,
  options?: ImageEditOptions
): boolean {
  if (editor.isDestroyed) return false;
  const hit = findImage(editor, (node) => node.attrs.src === src);
  if (!hit) return false;
  const { tr } = editor.state;
  tr.delete(hit.pos, hit.pos + hit.nodeSize);
  if (options?.addToHistory === false) tr.setMeta('addToHistory', false);
  editor.view.dispatch(tr);
  return true;
}

export function setImageAttrsAt(
  editor: Editor,
  pos: number,
  attrs: Record<string, unknown>,
  expectedSrc?: string
): boolean {
  if (editor.isDestroyed) return false;
  const node = editor.state.doc.nodeAt(pos);
  if (!node || node.type.name !== 'image') return false;
  if (expectedSrc !== undefined && node.attrs.src !== expectedSrc) return false;
  const { tr } = editor.state;
  editor.view.dispatch(tr.setNodeMarkup(pos, undefined, { ...node.attrs, ...attrs }));
  return true;
}

export function removeImageAt(editor: Editor, pos: number, expectedSrc?: string): boolean {
  if (editor.isDestroyed) return false;
  const node = editor.state.doc.nodeAt(pos);
  if (!node || node.type.name !== 'image') return false;
  if (expectedSrc !== undefined && node.attrs.src !== expectedSrc) return false;
  const { tr } = editor.state;
  editor.view.dispatch(tr.delete(pos, pos + node.nodeSize));
  return true;
}

// Na de pending-uploads-guard is een overgebleven blob-node per definitie dood: een undo tijdens
// een upload gevolgd door een latere redo kan zo'n node terugbrengen. Opruimen mag dan altijd,
// buiten de undo-geschiedenis om, anders belandt een blob:-URL in de opgeslagen content.
export function removeBlobImages(editor: Editor): number {
  if (editor.isDestroyed) return 0;

  const hits: ImageHit[] = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'image' && typeof node.attrs.src === 'string' && node.attrs.src.startsWith('blob:')) {
      hits.push({ pos, nodeSize: node.nodeSize, attrs: node.attrs });
    }
    return true;
  });

  if (hits.length === 0) return 0;

  const { tr } = editor.state;
  for (let i = hits.length - 1; i >= 0; i--) {
    const hit = hits[i];
    tr.delete(hit.pos, hit.pos + hit.nodeSize);
  }
  tr.setMeta('addToHistory', false);
  editor.view.dispatch(tr);
  return hits.length;
}
